import { Client } from "@/Common/Api/Client";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ContactDirectoryClient } from "@/ContactDirectory/Api/ContactDirectoryClient";
import { DirectoryEntityType, DirectoryHealthCareProviderSearchDto } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { PhoneNumber } from "../Api/Contacts/Dto/PhoneNumber";
import { FlatLookupView } from "../Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { Patient } from "../Models/Patient/Patient";
import { IPatientBannerModel } from "../Models/Patient/UI/IPatientBannerModel";
import { FormattedPhoneNumber } from "../Models/Portal/Patient/FormattedPhoneNumber";
import { ValidationHelper } from "./ValidationHelper";

export class PatientBannerHelper {
    /** Returns the default patient banner model. */
    public static defaultPatientBannerModel(): IPatientBannerModel {
        return Immutable.fromJS({
            Model: null,
            PatientDropdownVisible: false,
            Warnings: []
        });
    }

    /**
     * Load the patient banner model.
     * @param patientContactId The patient contact ID.
     * @param onPatientBannerModelLoaded A delegate called whenever new data have been loaded into the model.
     * */
    public static async loadPatientBannerModel(
        patientContactId: string,
        onPatientBannerModelLoaded: (model: IPatientBannerModel) => void = null,
        includeRelationships: boolean = false
    ): Promise<IPatientBannerModel> {
        const patient = await this.loadPatientModel(patientContactId, includeRelationships);
        let model = PatientBannerHelper.defaultPatientBannerModel();

        if (patient) {
            // add patient data
            model = model.set("Model", patient);
            onPatientBannerModelLoaded?.(model);

            await PatientBannerHelper.loadRamqInfo(patient);
            onPatientBannerModelLoaded?.(model);
        }

        return model;
    }

    /**
     * Load the patient model.
     * @param patientContactId The patient contact ID.
     * */
    public static async loadPatientModel(patientContactId: string, includeRelationships: boolean = false): Promise<Patient> {
        const response = await Client.postAsync<any>(
            ServerOperations.Server.Myle_LoadPatient,
            {
                IndividualId: patientContactId,
                IncludeRelatedRecalls: true,
                IncludeRelationships: includeRelationships
            }
        );

        if (!response.isSuccess) {
            return null;
        }

        const patient = Patient.fromResponseData(response.data);

        if (!cacheStore.ClinicSettings.EnableContactDirectory)
            return patient;

        if (patient.PreferredPharmacy.DirectoryPharmacyId)
            await patient.PreferredPharmacy.loadDirectoryPharmacy();

        if (patient.IntervenorsOnFile?.any(p => !!p.Model.Intervenor.HealthCareProviderId)) {
            try {
                const client = ContactDirectoryClient.getInstance();
                const healthCareProviders: DirectoryHealthCareProviderSearchDto[] = await client.getByIds(
                    DirectoryEntityType.HealthcareProvider,
                    patient.IntervenorsOnFile.map(p => p.Model.Intervenor.HealthCareProviderId).filter(p => !!p)
                );
                for (let intervenor of patient.IntervenorsOnFile) {
                    const healthCareProvider = healthCareProviders.firstOrDefault(p => p.id === intervenor.Model.Intervenor.HealthCareProviderId);
                    if (!healthCareProvider)
                        continue;

                    intervenor.Model.HealthCareProvider = healthCareProvider;
                }
            }
            catch (e) {
                console.error(e);
            }
        }
        return patient;
    }

    /**
     * Load the RAMQ information for a given patient.
     * @param patient The patient.
     * */
    public static async loadRamqInfo(patient: Patient): Promise<void> {
        const response = await Client.postAsync<RAMQInfo>(
            ServerOperations.Server.RAMQ_CheckSubscription,
            {
                Patient: {
                    IndividualID: patient.IndividualResponse.Contact_id
                },
                CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
            }
        );

        if (response.isSuccess) {
            const data = new RAMQInfo().FromResponseData(response.data);

            if (data)
                patient.setRamq(data);
        }
    }

    public static getFormattedPhoneNumber(phone: PhoneNumber, validationHelper: ValidationHelper): FormattedPhoneNumber {
        const result = new FormattedPhoneNumber();
        const phoneCountryLookupCode = cacheStore.GetLookupById(phone.CountryLookupId).LookupCode;
        let phoneCountryLookup: FlatLookupView;
        if (phoneCountryLookupCode == null)
            phoneCountryLookup = cacheStore.FindLookupByLookupCode("CNCANADA"); // default code = canada;
        else
            phoneCountryLookup = cacheStore.FindLookupByLookupCode(phoneCountryLookupCode);

        const contactType = cacheStore.GetLookupById(phone.ContactPointTypeLookupId).GetValue();
        const phoneType = cacheStore.GetLookupById(phone.PhoneTypeLookupId).GetValue();

        const validationRule = validationHelper.getPhoneNumberValidationRule(phoneCountryLookup.LookupID, null); // no need for region when checking for phone number format?
        const displayFormatSelector: string = validationRule ? validationRule.DisplayFormatSelectorPatern : "";
        const displayFormatSubstitution: string = (validationRule) ? validationRule.DisplayFormatSubstitution : "";
        let formattedPhone = ValidationHelper.cleanPhoneNumber(phone.Number, phoneCountryLookup.LookupID);
        formattedPhone = validationHelper.getFormatedString(formattedPhone, displayFormatSelector, displayFormatSubstitution);

        result.FormattedPhoneNumber = formattedPhone;
        result.ContactType = contactType;
        result.PhoneType = phoneType;

        return result;
    }
}
